import React, { useState, useEffect } from "react";
import "./blog.css";
import "../../Tailwind.css";
import ClipLoader from "react-spinners/ClipLoader";
import useBlogPosts from "../../hooks/useBlogPosts";
import { Navbar } from "../landingPage/components";
import { FeaturedPosts } from "./components/FeaturedPosts";
import { SidebarPosts } from "./components/SidebarPosts";
import { AllPosts } from "./components/AllPosts";

function Blog() {
  const { blogPosts, loadingBlogPosts } = useBlogPosts({
    order_by: "published_at",
  });

  const [bannerVideo, setBannerVideo] = useState(null);
  const [aspectRatio, setAspectRatio] = useState("1440/144");

  useEffect(() => {
    return loadBannerVideoByWidth(setBannerVideo, setAspectRatio);
  }, []);

  const { featuredPosts, regularPosts } = processBlogPosts(blogPosts);

  if (loadingBlogPosts) {
    return (
      <div className="flex justify-center items-center h-64">
        <ClipLoader color="#ce154e" loading={true} size={50} />
      </div>
    );
  }

  return (
    <>
      <Navbar />
      <div className="w-full flex flex-col items-center justify-center gap-6 md:gap-10">
        <div className="rounded-xl mt-4 md:mt-5 pt-6 md:pt-10 bg-deep-space-lighter w-full md:w-[80%] 2xl:w-[70%]">
          <header>
            <h1 className="font-bold text-brandpink text-2xl md:text-4xl text-center uppercase mb-6 md:mb-8 px-4 md:px-8">
              Orion Sinais Blog
            </h1>
          </header>
          <div className="flex flex-col lg:flex-row px-4 md:px-8 gap-6 lg:gap-8 xl:gap-2 mb-4">
            <section className="w-full lg:w-9/12 xl:w-10/12 pb-8 lg:pb-12">
              <FeaturedPosts posts={featuredPosts} />
            </section>
            <div className="hidden lg:block h-auto w-[2px] bg-brandpink mx-2" />
            <aside className="w-full lg:w-3/12 xl:w-2/12 pb-6 lg:pb-0">
              <h2 className="text-center text-lg md:text-xl font-bold mb-4 text-brandpink uppercase">
                Recentes
              </h2>
              <SidebarPosts posts={regularPosts} />
            </aside>
          </div>
        </div>

        {bannerVideo && (
          <div className="w-full relative" style={{ aspectRatio: aspectRatio }}>
            <img
              src={bannerVideo}
              className="absolute inset-0 w-full h-full object-cover"
              alt="Decorative banner"
              style={{ pointerEvents: "none" }}
            />
          </div>
        )}

        <div className="rounded-xl w-[90%] md:w-[80%] lg:w-[70%]">
          <header>
            <h1 className="font-bold text-brandpink text-2xl md:text-4xl text-center uppercase mb-6 md:mb-8 px-4 md:px-8">
              Todos
            </h1>
          </header>
          <div className="flex flex-row">
            <section className="w-full pb-8 md:pb-12">
              <AllPosts posts={blogPosts} />
            </section>
          </div>
        </div>
      </div>
    </>
  );
}

function processBlogPosts(blogPosts) {
  const empty = { featuredPosts: [], regularPosts: [] };

  return (
    blogPosts?.reduce((acc, post) => {
      if (post.is_featured) {
        acc.featuredPosts.push(post);
      }
      if (acc.regularPosts.length < 8) {
        acc.regularPosts.push(post);
      }
      return acc;
    }, empty) || empty
  );
}

function loadBannerVideoByWidth(setBannerVideo, setAspectRatio) {
  const bannersOptions = [
    {
      minWidth: 0,
      maxWidth: 480,
      aspectRatio: "480/96",
      filename: "banner-480-96.gif",
    },
    {
      minWidth: 481,
      maxWidth: 768,
      aspectRatio: "768/144",
      filename: "banner-768-144.gif",
    },
    {
      minWidth: 769,
      maxWidth: 1024,
      aspectRatio: "1024/144",
      filename: "banner-1024-144.gif",
    },
    {
      minWidth: 1025,
      maxWidth: Infinity,
      aspectRatio: "1440/144",
      filename: "banner-1440-144.gif",
    },
  ];

  const loadBannerVideo = () => {
    requestAnimationFrame(() => {
      const width = window.innerWidth;

      const bannerSettings =
        bannersOptions.find(
          (option) => width >= option.minWidth && width <= option.maxWidth,
        ) || bannersOptions.at(-1);

      const loadBanner = (filename) =>
        import(`../../assets/banners/${filename}`);

      loadBanner(bannerSettings.filename).then((module) => {
        setBannerVideo(module.default);
        setAspectRatio(bannerSettings.aspectRatio);
      });
    });
  };

  loadBannerVideo();

  let resizeTimer;
  const handleResize = () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(loadBannerVideo, 250);
  };

  window.addEventListener("resize", handleResize);
  return () => window.removeEventListener("resize", handleResize);
}

export default Blog;
